<template>
  <div @keydown.esc="endPolygon(); clearSelection();" tabindex="0">

    <!-- Polgon control buttons -->
    <div
      v-if="isTagSelected || isSelected"
      class="polygon-controls"
      :class="{'polygon-controls--hidden': !showPolygonControls}"
    >
      <div class="polygon-controls__input">
        <button
          v-if="!isCreatingPolygon && polygon.points.length === 0"
          class="btn-control"
          @click.stop="startPolygon"
        >
          Create Polygon
        </button>
    
        <button
          v-else
          class="btn-control btn-control--red"
          @click.stop="deletePolygon"
        >
          Delete Polygon
        </button>

        <template v-if="isSelected">
          <button
            v-if="!isAddingPoints && !isEditingPoints"
            class="btn-control"
            @click.stop="isAddingPoints = true"
          >
            Add Points
          </button>
          <button
            v-if="!isAddingPoints && !isEditingPoints"
            class="btn-control"
            @click.stop="isEditingPoints = true"
          >
            Edit Points
          </button>
          <button
            v-if="isEditingPoints || isAddingPoints"
            class="btn-control btn-control--yellow"
            @click.stop="isEditingPoints = false; isAddingPoints = false"
          >
            Stop Editing
          </button>
        </template>
        <button
          v-if="isCreatingPolygon && polygon.points.length > 1"
          class="btn-control ml-1"
          @click.stop="endPolygon"
        >
          Close Polygon
        </button>
      </div>
    </div>

    <button
      v-if="isTagSelected || isSelected"
      class="btn-hide-controls"
      :title="showPolygonControls ? 'Hide controls' : 'Show Controls'"
      @click="showPolygonControls = !showPolygonControls"
    >
      <svg v-if="showPolygonControls" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-chevron-up"><polyline points="18 15 12 9 6 15"></polyline></svg>
      <svg v-if="!showPolygonControls" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="feather feather-chevron-down"><polyline points="6 9 12 15 18 9"></polyline></svg>
    </button>

    <!-- Display polygon -->
    <div
      :title="!isCreatingPolygon ? title : ''"
      class="polygon-overlay"
      :class="[
        {'polygon-overlay--active': isSelected || isCreatingPolygon},
        {'polygon-overlay--outline': isSelected && !isCreatingPolygon},
        {'polygon-overlay--outline-dashed': isTagSelected && !isCreatingPolygon},
      ]"
      ref="polygonOverlay"
      @mouseup.left.stop="editPointDragEnd($event)"
      @mousemove.left.stop="editPointDrag($event)"
    >
      <svg
        v-if="isCreatingPolygon"
        viewBox="0 0 3190 1960"
        height="100%"
        width="100%"
        fill="transparent"
        stroke="#FF7F50"
        stroke-width="1"
      >
        <polygon :points="pointToPolygon"/>
      </svg> 
      <svg
        v-if="!isCreatingPolygon"
        viewBox="0 0 3190 1960"
        height="100%"
        width="100%"
        :fill="getFill(editPolygon)"
        :stroke="editPolygon.stroke"
        :stroke-width="editPolygon.strokeWidth"
      >
        <polygon
          :class="[
            {'drop-shadow' : editPolygon.dropShadow},
            {'inactive' : isCreatingPolygon},
          ]"
          :points="pointToPolygon"
          :style="`outline-offset: ${15 + editPolygon.strokeWidth}px`"
          @click.stop="selectPolygon"
        />
      </svg> 

      <div v-if="isEditingPoints">
        <div
          v-for="(point, i) in editPolygon.points"
          :key="'point' + i"
          class="point"
          :style="`left: ${point[0] * scale}px; top: ${point[1] * scale}px;`"
          @mousedown.left.stop="editPointDragStart(i)"
          @mouseup.left.stop="editPointDragEnd"
          @dblclick.left.stop="deletePoint(i)"
        ></div>
      </div>

      <div v-if="isAddingPoints">
        <div
          v-for="(point, i) in editPolygon.points"
          :key="'dot' + i"
          class="dot"
          :style="`left: ${point[0] * scale}px; top: ${point[1] * scale}px;`"
        ></div>
        <div
          v-for="(point, i) in intermidiatePoints"
          :key="'plus' + i"
          class="plus"
          :style="`left: ${point[0] * scale}px; top: ${point[1] * scale}px;`"
          @click.left="addPointToPolygon(point, i)"
        ></div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    'polygon': {
      type: Object,
      default: () => ({
        fill: 'rgba(0,0,0,0)',
        stroke: 'rgba(255,255,255,0)',
        strokeWidth: 8,
        points: [],
        dropShadow: false,
      })
    },
    'scale': {
      type: Number,
      default: 1
    },
    'isSelected': {
      type: Boolean,
      default: false
    },
    'isTagSelected': {
      type: Boolean,
      default: false
    },
    'title': {
      type: String,
      default: ''
    },
  },

  data () {
    return {
      isCreatingPolygon: false,
      isEditingPoints: false,
      isAddingPoints: false,

      editPolygonUpdate: 0,
      editPolygon: {},
      editPolygonPointIndex: null,
      intermidiatePoints: [],

      activePicker: '',

      showPolygonControls: true
    }
  },

  computed: {
    pointToPolygon () {
      if (this.editPolygon && this.editPolygon.points) {
        return this.editPolygon.points.map(point => `${point[0]},${point[1]}`).join(' ')
      }
    },
  },

  watch: {
    'editPolygon': {
      handler: function (newPolygon) {
        if (newPolygon) {
          this.generateIntermidiatePoints()
          this.$emit('updatePolygon', newPolygon)
        }
      },
      deep: true
    },

    'isTagSelected': {
      handler: function (newIsTagSelected) {
        if (!newIsTagSelected) {
          this.isEditingPoints = false
          this.isAddingPoints = false
        }
      },
      deep: true
    },
  },

  mounted () {
    this.editPolygon = this.polygon
    this.generateIntermidiatePoints()
  },

  methods: {
    generateIntermidiatePoints () {
      this.intermidiatePoints = []
      this.editPolygon.points.forEach((point, i) => {
        if (i !== 0 && i !== this.editPolygon.points.length) {
          let prevPoint = this.editPolygon.points[i - 1]
          let x = (prevPoint[0] + point[0]) / 2
          let y = (prevPoint[1] + point[1]) / 2
          this.intermidiatePoints.push([x, y])
        }

        if (i === this.editPolygon.points.length - 1) {
          let firstPoint = this.editPolygon.points[0]
          let x = (firstPoint[0] + point[0]) / 2
          let y = (firstPoint[1] + point[1]) / 2
          this.intermidiatePoints.push([x, y])
        }
      })
    },

    selectPolygon () {
      if (!this.isCreatingPolygon) {
        this.$emit('selectedPolygon', this.polygon)
      }
    },

    startPolygon () {
      this.isCreatingPolygon = true
      this.editPolygon.points.push([0, 0])
    },

    endPolygon () {
      console.log('end')
      
      if (this.isCreatingPolygon) {
        this.editPolygon.points.pop()
      }
      this.isCreatingPolygon = false
    },

    addPointToPolygon (point, i) {
      this.editPolygon.points.splice(i + 1, 0, point)
      this.generateIntermidiatePoints()
    },

    deletePoint (i) {
      this.editPolygon.points.splice(i, 1)
      this.generateIntermidiatePoints()
    },

    editPointDragStart (i) {
      this.editPolygonPointIndex = i
    },

    editPointDragEnd (e) {
      if (this.isCreatingPolygon) {
        const x = e.layerX / this.scale
        const y = e.layerY / this.scale

        this.editPolygon.points.push([x, y])
      } else if (this.isEditingPoints || this.isAddingPoints) {
        this.editPolygonPointIndex = null
      } else {
        this.$emit('clearSelection')
      }
    },

    editPointDrag (event) {
      if (this.isCreatingPolygon && this.editPolygon.points.length > 0) {
        const x = event.layerX / this.scale
        const y = event.layerY / this.scale

        this.editPolygon.points[this.editPolygon.points.length - 1][0] = x
        this.editPolygon.points[this.editPolygon.points.length - 1][1] = y

        this.$set(this.editPolygon.points, this.editPolygon.points.length - 1, [x ,y])
      }

      if ((this.isEditingPoints || this.isAddingPoints) && this.editPolygonPointIndex !== null) {
        let point = this.editPolygon.points[this.editPolygonPointIndex]

        point[0] = Math.round(point[0] + event.movementX / this.scale)
        point[1] = Math.round(point[1] + event.movementY / this.scale)

        this.$set(this.editPolygon.points, this.editPolygonPointIndex, point)
      }
    },

    deletePolygon () {
      if (window.confirm("Are you sure you want to delete this polygon?")) {
        this.editPolygon.points = []
        this.clearSelection()
      }
    },

    getFill (polygon) {
      const fill = polygon.fill
      const stroke = polygon.stroke
      let fillOpacity = ''
      let strokeOpacity = ''

      if (fill) {
        fillOpacity = fill.replace(/^.*,(.+)\)/,'$1')
      }

      if (stroke) {
        strokeOpacity = stroke.replace(/^.*,(.+)\)/,'$1')
      }

      if (fillOpacity === '0' && strokeOpacity === '0') {
        return 'rgba(0,0,0,.25)'
      }

      return fill
    },

    clearSelection () {
      this.isEditingPoints = false
      this.isAddingPoints = false
      this.isCreatingPolygon = false
      this.editPolygonPointIndex = null

      this.$emit('clearSelection')
    }
  },
}
</script>

<style lang="scss" scoped>
.polygon-overlay {
  pointer-events: none;
  position: absolute;
  height: 100%;
  left: 0;
  top: 0;
  width: 100%;
  z-index: 5;

  &--active {
    // cursor: crosshair;
    pointer-events: auto;
    z-index: 10;
  }

  &--outline-dashed {
    svg polygon {
      outline: 5px dashed aqua;
    }
  }

  &--outline {
    z-index: 100;
    svg polygon {
      outline: 5px solid aqua;
    }
  }

  svg polygon {
    pointer-events: auto;
  }

  .drop-shadow {
    filter: drop-shadow(10px 10px 10px black);
  }

  .inactive {
    pointer-events: none;
  }
}

.point {
  position: absolute;
  width: 16px;
  height: 16px;
  background: transparent;
  backdrop-filter: invert(100%);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  z-index: 20;

  &::before {
    content: '';
    height: 9px;
    width: 9px;
    height: 4px;
    width: 4px;
    // background: transparent;
    background: yellow;
    // backdrop-filter: invert(100%);
    border-radius: 50%;
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
  }
}
.dot {
  position: absolute;
  width: 6px;
  height: 6px;
  background: #000;
  border: 1px solid #fff;
  border-radius: 50%;
  pointer-events: none;
  transform: translate(-3px, -3px);
  z-index: 20;
}

.plus {
  align-items: center;
  background: #fff;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  justify-content: center;
  height: 14px;
  position: absolute;
  transform: translate(-7px, -7px);
  width: 14px;
  z-index: 20;
  font-size: 14px;
  color: #000;

  &::after {
    content: '+';
    display: flex;
    line-height: 0;
    padding: 0 0 3px 1px;
  }
}

.polygon-controls {
  display: flex;
  justify-content: space-between;
  padding: 0 10px;
  pointer-events: all;
  position: absolute;
  left: 50%;
  top: 4px;
  transform: translateX(-50%);
  transition: all 300ms ease-in-out;
  z-index: 200;

  &--hidden {
    top: -24px;
  }

  .btn-control {
    appearance: none;
    background-color: #3182ce;
    border: none;
    border-radius: 9999px;
    color: #fff;
    font-size: 12px;
    height: 24px;
    margin: 0 4px;
    padding: 0 8px;

    &:hover {
      background: #2b6cb0;
    }

    &--red {
      background-color: #ce3131;

      &:hover {
        background: #ac2424;
      }
    }

    &--yellow {
      background-color: #e0ce2d;
      color: #000;

      &:hover {
        background: #b29b08;
      }
    }
  }
}

.btn-hide-controls {
  align-items: center;
  appearance: none;
  background: #3182ce;
  border: none;
  border-radius: 50%;
  color: #fff;
  display: flex;
  font-size: 14px;
  justify-content: center;
  height: 24px;
  right: 32px;
  pointer-events: all;
  position: absolute;
  top: 4px;
  width: 24px;
  z-index: 110;

  &:hover {
    background: #2b6cb0;
  }
}
</style>